// src/ui/loading-overlay.js — overlay de carga a pantalla completa (boot + Tribuna).
//
// GARANTÍA DETERMINÍSTICA (smoke 7-jun): UNA sola foto a la vez, nunca dos.
//  - Foto FRESCA (≠ la última) solo cuando el overlay pasa de OCULTO→VISIBLE
//    (cada navegación a la Tribuna que viene de oculto). Si el overlay YA está
//    visible (boot arriba y se entra a la Tribuna en la misma carga), se MANTIENE
//    la foto actual → sin swap visible.
//  - UN solo overlay + UNA sola <img>, reusados SIEMPRE (solo se cambia el src
//    antes de mostrar). El hide NO remueve el overlay (solo lo desvanece) → es
//    imposible que coexistan dos elementos o dos imágenes solapadas en el fundido.
//
// Foto en COLUMNA centrada (max-width 480px = #app-root); el #0E1620 llena los
// márgenes en desktop. Precarga + decode antes de mostrar (sin pop-in); salida
// por opacity. Lista bundleada (cero red en runtime); webp en /loading/.
import { LOADING_IMAGES } from './loading-images.generated.js';
import { withTimeout } from '../utils/timeout.js';

const LS_KEY = 'pm_loading_last_idx';
const DECODE_TIMEOUT_MS = 4000;
const APP_MAX_WIDTH = '480px'; // === #app-root max-width (css/styles.css)

// Elige un índice DISTINTO al último mostrado, sin loop (uniforme sobre los
// otros n-1). Puro y testeable (rnd inyectable). Garantiza idx !== lastIdx (n>1).
export function pickIndex(n, lastIdx, rnd = Math.random) {
  if (n <= 0) return -1;
  if (n === 1) return 0;
  const hasLast = Number.isInteger(lastIdx) && lastIdx >= 0 && lastIdx < n;
  if (!hasLast) return Math.floor(rnd() * n);
  let idx = Math.floor(rnd() * (n - 1));
  if (idx >= lastIdx) idx += 1;
  return idx;
}

// Elige una foto FRESCA (≠ la última), actualizando el "last idx". Se llama solo
// cuando el overlay pasa de oculto→visible → variedad en cada re-entrada.
export function pickLoadingImage() {
  const n = LOADING_IMAGES.length;
  if (n === 0) return null;
  let last = -1;
  try { last = parseInt(localStorage.getItem(LS_KEY) ?? '-1', 10); } catch { /* sin storage */ }
  const idx = pickIndex(n, Number.isInteger(last) ? last : -1);
  try { localStorage.setItem(LS_KEY, String(idx)); } catch { /* sin storage */ }
  return LOADING_IMAGES[idx];
}

// Fuerza el layout por longhand (no `inset`, por WebViews viejos). La foto vive en
// una columna centrada (max-width = #app-root) para no zoomearse en desktop.
function applyStyles(el, col, img) {
  Object.assign(el.style, {
    position: 'fixed', top: '0', left: '0', right: '0', bottom: '0',
    width: '100%', height: '100%', zIndex: '9999', background: '#0E1620',
    display: 'flex', justifyContent: 'center',
  });
  if (col) Object.assign(col.style, {
    position: 'relative', width: '100%', maxWidth: APP_MAX_WIDTH, height: '100%',
    overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  });
  if (img) Object.assign(img.style, {
    position: 'absolute', top: '0', left: '0', width: '100%', height: '100%',
    objectFit: 'cover', display: 'block',
  });
}

// Crea (o REUSA) el único overlay → columna → única <img>. En el boot ya viene en
// index.html; en la Tribuna se crea acá. Nunca duplica. Devuelve {el, img}.
function ensureOverlay() {
  if (typeof document.createElement !== 'function') return null;
  let el = document.getElementById('pm-loading-overlay');
  if (!el) {
    el = document.createElement('div');
    if (!el || !el.classList || typeof el.appendChild !== 'function') return null;
    el.id = 'pm-loading-overlay';
    el.innerHTML = '<div id="pm-loading-col"><img id="pm-loading-img" alt="" aria-hidden="true"><div id="pm-loading-tag">Cargando&hellip;</div></div>';
    document.body.appendChild(el);
  }
  if (!el.classList) return null;
  let col = el.querySelector ? el.querySelector('#pm-loading-col') : null;
  if (!col && typeof document.createElement === 'function') {
    col = document.createElement('div');
    if (col && col.classList) { col.id = 'pm-loading-col'; el.appendChild(col); }
  }
  const host = col || el;
  // Garantizar UNA sola <img> (defensivo contra duplicados accidentales).
  const imgs = host.querySelectorAll ? host.querySelectorAll('#pm-loading-img, img') : [];
  for (let i = 1; i < imgs.length; i++) imgs[i].remove?.();
  let img = host.querySelector ? host.querySelector('#pm-loading-img') || host.querySelector('img') : null;
  if (!img && typeof document.createElement === 'function') {
    img = document.createElement('img');
    if (img && img.classList) { img.id = 'pm-loading-img'; img.alt = ''; img.setAttribute('aria-hidden', 'true'); host.insertBefore(img, host.firstChild); }
  }
  applyStyles(el, col, img);
  return { el, img };
}

// Muestra el overlay. Foto FRESCA si venía oculto; MANTIENE la actual si ya está
// visible (evita swap en boot→primera Tribuna). Reusa overlay/img. Defensivo.
export async function showLoadingOverlay() {
  try {
    if (typeof document === 'undefined' || !document.body || LOADING_IMAGES.length === 0) return;
    // Estado ANTES de tocar nada: ¿el overlay ya está visible con una foto?
    const prev = document.getElementById('pm-loading-overlay');
    const prevImg = prev && prev.querySelector ? prev.querySelector('#pm-loading-img') : null;
    const prevSrc = prevImg && prevImg.getAttribute ? prevImg.getAttribute('src') : '';
    const yaVisible = !!(prev && prev.classList && !prev.classList.contains('pm-hiding') && prevSrc);
    // Fresca solo al pasar de oculto→visible; si ya está visible, mantener.
    const path = yaVisible ? prevSrc : pickLoadingImage();
    if (!path) return;
    const refs = ensureOverlay();
    if (!refs || !refs.el || !refs.el.classList) return; // DOM no soportado → no-op
    const { el, img } = refs;
    el.classList.remove('pm-hiding');
    if (!img || !img.classList) return;
    if (img.getAttribute && img.getAttribute('src') === path) { img.classList.add('pm-ready'); return; } // ya cargada
    try {
      const pre = new Image();
      pre.src = path;
      if (pre.decode) await withTimeout(pre.decode(), DECODE_TIMEOUT_MS, 'loading-decode');
    } catch { /* decode lento/falla: mostramos igual */ }
    img.src = path;
    const reveal = () => img.classList.add('pm-ready');
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(reveal));
    else reveal();
  } catch { /* overlay cosmético: nunca romper el render */ }
}

// Oculta el overlay por opacity. NO lo remueve (queda en el DOM, pointer-events
// none) → el mismo elemento se reusa si vuelve a mostrarse. Cero recreación.
export function hideLoadingOverlay() {
  try {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('pm-loading-overlay');
    if (el && el.classList) el.classList.add('pm-hiding');
  } catch { /* idem: nunca romper */ }
}

// Boot: si el overlay ya está en el DOM (index.html), llenarlo con la foto apenas
// se importa el módulo. El fondo sólido del overlay ya tapa la card sin flash.
if (typeof document !== 'undefined' && document.getElementById('pm-loading-overlay')) {
  showLoadingOverlay();
}
